/**
 * Fleet-by-tenant table (TMMPDM-0020): per-tenant money in the tenant's currency; one totals
 * row per `totals[]` currency — never a cross-currency sum (MM-D-100).
 */
import {
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { Amount } from '@/components/ui';
import { EmptyState, ErrorState } from '@/components/page';
import { formatDate, formatNumber } from '@/format';
import type { TenantDashboard } from '@/api/tmm/types';
import type { FleetData } from './useFleetDashboard';

interface FleetTableProps {
  data: FleetData | null;
  loading: boolean;
  error: boolean;
  failure: unknown;
  onRetry: () => void;
  locale: string;
}

function signedOrDash(n: number, locale: string, plus: boolean): string {
  if (n === 0) return '—';
  const formatted = formatNumber(n, locale);
  return plus ? `+${formatted}` : formatted;
}

export function FleetTable({ data, loading, error, failure, onRetry, locale }: FleetTableProps) {
  const { t } = useCopy();

  if (error && !data) {
    return (
      <ErrorState
        error={failure}
        message={t('dashboard.loadError', 'Could not load the fleet dashboard.')}
        onRetry={onRetry}
      />
    );
  }

  const header = (
    <TableHead>
      <TableRow>
        <TableCell>{t('dashboard.fleet.col.tenant', 'Tenant')}</TableCell>
        <TableCell align="right">{t('dashboard.fleet.col.subscribers', 'Subscribers')}</TableCell>
        <TableCell align="right">{t('dashboard.fleet.col.new', 'New')}</TableCell>
        <TableCell align="right">{t('dashboard.fleet.col.churned', 'Churned')}</TableCell>
        <TableCell align="right">{t('dashboard.fleet.col.retail', 'Retail')}</TableCell>
        <TableCell align="right">{t('dashboard.fleet.col.wholesale', 'Wholesale')}</TableCell>
        <TableCell align="right">{t('dashboard.fleet.col.uplift', 'Uplift')}</TableCell>
        <TableCell align="right">{t('dashboard.fleet.col.tenantShare', 'Tenant share')}</TableCell>
        <TableCell align="right">{t('dashboard.fleet.col.platform', 'Platform')}</TableCell>
        <TableCell align="right">{t('dashboard.fleet.col.nextClose', 'Next close')}</TableCell>
      </TableRow>
    </TableHead>
  );

  if (!data) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('dashboard.fleet.tableAria', 'Fleet by tenant')}>
          {header}
          <TableBody>
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {loading ? t('common.loading', 'Loading') : null}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  const { dashboard, tenantsBySlug } = data;
  const rows = dashboard.tenants;

  if (rows.length === 0) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('dashboard.fleet.tableAria', 'Fleet by tenant')}>
          {header}
          <TableBody>
            <TableRow>
              <TableCell colSpan={10}>
                <EmptyState title={t('dashboard.fleet.empty', 'No tenants yet.')} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  const money = (row: TenantDashboard, minor: number) => {
    const currency = row.slug ? tenantsBySlug.get(row.slug)?.currency : undefined;
    return currency ? <Amount minor={minor} currency={currency} locale={locale} /> : '—';
  };

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('dashboard.fleet.tableAria', 'Fleet by tenant')}>
          {header}
          <TableBody>
            {rows.map((row, index) => {
              const slug = row.slug;
              const tenant = slug ? tenantsBySlug.get(slug) : undefined;
              return (
                <TableRow key={slug ?? `tenant-${index}`} hover>
                  <TableCell>
                    <Box>
                      {slug ? (
                        <Link
                          component={RouterLink}
                          to={`/partners/by-tenant/${encodeURIComponent(slug)}`}
                          variant="body2"
                          underline="hover"
                        >
                          {tenant?.name ?? slug}
                        </Link>
                      ) : (
                        <Typography variant="body2">{tenant?.name ?? '—'}</Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={(theme) => ({ display: 'block', ...theme.typography.mono })}
                      >
                        {slug ?? '—'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.subscribers.active} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    {signedOrDash(row.subscribers.newInPeriod, locale, true)}
                  </TableCell>
                  <TableCell align="right">
                    {signedOrDash(row.subscribers.churnedInPeriod, locale, false)}
                  </TableCell>
                  <TableCell align="right">{money(row, row.revenue.retailMinor)}</TableCell>
                  <TableCell align="right">{money(row, row.revenue.wholesaleMinor)}</TableCell>
                  <TableCell align="right">{money(row, row.revenue.upliftMinor)}</TableCell>
                  <TableCell align="right">{money(row, row.revenue.tenantShareMinor)}</TableCell>
                  <TableCell align="right">{money(row, row.revenue.platformShareMinor)}</TableCell>
                  <TableCell align="right">{formatDate(row.payouts.nextCloseOn, locale)}</TableCell>
                </TableRow>
              );
            })}
            {dashboard.totals.map((entry) => (
              <TableRow key={`total-${entry.currency}`}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('dashboard.fleet.totalsInCurrency', 'Totals in {{currency}}', {
                      currency: entry.currency,
                    })}
                  </Typography>
                </TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">
                  <Amount
                    minor={entry.revenue.retailMinor}
                    currency={entry.currency}
                    locale={locale}
                  />
                </TableCell>
                <TableCell align="right">
                  <Amount
                    minor={entry.revenue.wholesaleMinor}
                    currency={entry.currency}
                    locale={locale}
                  />
                </TableCell>
                <TableCell align="right">
                  <Amount
                    minor={entry.revenue.upliftMinor}
                    currency={entry.currency}
                    locale={locale}
                  />
                </TableCell>
                <TableCell align="right">
                  <Amount
                    minor={entry.revenue.tenantShareMinor}
                    currency={entry.currency}
                    locale={locale}
                  />
                </TableCell>
                <TableCell align="right">
                  <Amount
                    minor={entry.revenue.platformShareMinor}
                    currency={entry.currency}
                    locale={locale}
                  />
                </TableCell>
                <TableCell align="right" />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
